import React, { useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import OGEAvatar from '../OGE/OGEAvatar'
import ChatPill from './BottomRow/ChatPill'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  connectAdjudicationWebSocket,
  disconnectAdjudicationWebSocket,
} from '@/redux/asyncThunks/adjudicationMessageWebsocket'
import { SenderType } from '@/constants/chat'
import {
  clearMessages,
  setIsLoadingMessages,
} from '@/redux/slices/adjudicationMessageSlice'
import { useGamePhase } from '@/hooks/useGamePhase'
import { getAccessToken } from '@/utils/authUtils'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import Typing from '../Room/Typing'
import { formatDateRelative, formatTime } from '@/utils/dateTimeUtils'
import LlmModelBadge from '@/components/OGE/LlmModelBadge'

interface AdjudicationChatProps {
  selectedConversation: any
}

const AdjudicationChat: React.FC<AdjudicationChatProps> = ({
  selectedConversation,
}) => {
  const dispatch = useAppDispatch()
  const jwtToken = getAccessToken()
  const { phaseId, decisionPhase, manualMode } = useGamePhase()

  const { messages, isLoadingMessages } = useAppSelector(
    (state) => state.adjudicationMessage
  )

  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight
    }
  }, [messages, selectedConversation])

  const user = useSelector((state: RootState) => state.user.user)

  useEffect(() => {
    if (jwtToken && selectedConversation) {
      dispatch(clearMessages())
      dispatch(setIsLoadingMessages(true))

      const phaseIdToUse = manualMode
        ? decisionPhase?.id?.toString()
        : phaseId?.toString()

      dispatch(
        connectAdjudicationWebSocket({
          jwtToken,
          decisionId: selectedConversation.decision_id,
          phaseId: phaseIdToUse,
        })
      )
    }

    return () => {
      dispatch(disconnectAdjudicationWebSocket())
    }
  }, [dispatch, selectedConversation, jwtToken])

  const isStreaming = messages.some((message) => message.streaming)

  return (
    <div className='w-full min-w-0'>
      {selectedConversation && (
        <div className='mt-2 ml-2'>
          <strong>Conversation</strong>
        </div>
      )}
      <div
        ref={messagesEndRef}
        className='overflow-auto p-2 px-3 h-[calc(100vh-315px)] mb-1'
      >
        {selectedConversation && user ? (
          <>
            {isLoadingMessages ? (
              <p className='text-center'>Loading...</p>
            ) : (
              <>
                {messages?.map((message, index) => {
                  const showDate =
                    index === 0 ||
                    (message.created_at &&
                      messages[index - 1]?.created_at &&
                      formatDateRelative(message.created_at) !==
                        formatDateRelative(
                          messages[index - 1].created_at as string
                        ))
                  return (
                    <div key={`${message.id}-${index}`}>
                      {showDate && message.created_at && (
                        <div className='flex items-center justify-center my-4'>
                          <div className='bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full font-medium'>
                            {formatDateRelative(message.created_at)}
                          </div>
                        </div>
                      )}
                      <div
                        className={`flex mb-4 ${
                          message.senderName === user.email
                            ? 'justify-end'
                            : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[80%] p-4 rounded-[20px] bg-[#E6ECF1] ${
                            message.senderName === user.email
                              ? 'text-right'
                              : 'text-left'
                          }`}
                        >
                          {message.senderName !== user.email &&
                            message.senderType !== SenderType.Chatbot && (
                              <div className='flex items-center gap-2 mb-1'>
                                <OGEAvatar
                                  src='/images/avatar-image.svg'
                                  fallback={'AB'}
                                  variant='sm'
                                  isLoading={false}
                                />
                                <span className='text-dark-blue text-base font-medium'>
                                  {message.senderName}
                                </span>
                              </div>
                            )}
                          {message.senderType === SenderType.Chatbot && (
                            <div className='flex items-center gap-2 mb-1'>
                              <OGEAvatar
                                src={'/images/avatar-chatbot.svg'}
                                fallback={'AB'}
                                variant='sm'
                                isLoading={false}
                              />
                              <span className='text-dark-blue text-base font-medium'>
                                {message.senderName}
                              </span>
                            </div>
                          )}
                          <div className={`text-sm font-medium text-black`}>
                            <div className='chat-markdown prose'>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                                components={{
                                  a: ({ href, children }) => (
                                    <a
                                      href={href}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                    >
                                      {children}
                                    </a>
                                  ),
                                }}
                              >
                                {message.message}
                              </ReactMarkdown>
                            </div>
                            {message.streaming && '▋'}
                          </div>
                          {message.created_at && (
                            <div className='flex justify-end items-center gap-2 mt-2'>
                              {message.senderType === SenderType.Chatbot && (
                                <LlmModelBadge
                                  llmModelName={message.llm_model_name}
                                  llmProvider={message.llm_provider}
                                />
                              )}
                              <span className='text-xs text-gray-500 opacity-75'>
                                {formatTime(message.created_at)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
                {(() => {
                  const lastMsg = messages[messages.length - 1]
                  const isProfessorLast =
                    lastMsg &&
                    lastMsg.senderType === SenderType.Professor &&
                    lastMsg.senderName === user?.email
                  if (isProfessorLast && !isStreaming) {
                    return (
                      <div className='flex justify-start px-4 mb-4'>
                        <Typing />
                      </div>
                    )
                  }

                  return null
                })()}
                {messages.length == 0 && (
                  <div className='w-full h-full flex items-center justify-center'>
                    <div>
                      <p className='text-center text-xl text-gray-600'>
                        No messages yet
                      </p>
                      <p>
                        <span className='text-center text-gray-600'>
                          Start a conversation with Adjudication Bot
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <p className='text-center'>No Conversation Selected</p>
        )}
      </div>
      {selectedConversation && (
        <div className='px-2'>
          <ChatPill disabled={isStreaming} showActionButtons={false} />
        </div>
      )}
    </div>
  )
}

export default AdjudicationChat
